import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Brain, Zap, Moon, Star } from "lucide-react";

const BrainWaveActivity = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const brainWaves = [
    {
      title: "Beta Brainwave Pattern",
      frequency: "13-20 cycles/sec",
      description: (
        <>
          <p><ul><p>Waking state of external focus, active thinking, and problem-solving.</p></ul></p>
          <p><ul><p>Dominated by ego and mental activity before Yoga Nidra begins.</p></ul></p>
          <p><ul><p>Yoga Nidra will help transition away from stress and cognitive overload, to quieten the mind.</p></ul></p>
        </>
      ),
      icon: <Brain className="w-6 h-6 text-blue-800" />,
      gradient: "from-blue-50 to-white",
      color: "blue",
      iconBg: "bg-blue-200",
      badgeClasses: "bg-blue-100 text-blue-700",
    },
    {
      title: "Alpha Brainwave Pattern",
      frequency: "8-12 cycles/sec",
      description: (
        <>
          <ul>
            <li><p>Inner awareness increases as external stimuli fade, body relaxes as tensions release.</p></li>
            <li><p>Transition between waking and dreaming; mind is still, calm and safe.</p></li>
            <li><p>Conscious dreaming and deep relaxation takes place.</p></li>
          </ul>
          <p><br /></p>
        </>
      ),
      icon: <Zap className="w-6 h-6 text-green-800" />,
      gradient: "from-green-50 to-white",
      color: "green",
      iconBg: "bg-green-200",
      badgeClasses: "bg-green-100 text-green-700",
    },
    {
      title: "Theta Brainwave Pattern",
      frequency: "4-7 cycles/sec",
      description: (
        <>
          <p>
            <p>
              Deeper stages of Yoga Nidra with vivid
              imagery and heightened insight
            </p>
          </p>
          <p><p>Repressed desires, fears and patterns surface from the subconscious, allowing deep emotional processing.</p></p>
          <p>Ideal for neuroplastic change through sankalpa in Yoga Nidra.</p>
        </>
      ),
      icon: <Moon className="w-6 h-6 text-orange-800" />,
      gradient: "from-orange-50 to-white",
      color: "orange",
      iconBg: "bg-orange-200",
      badgeClasses: "bg-orange-100 text-orange-700",
    },
    {
      title: "Delta Brainwave Pattern",
      frequency: "0-4 cycles/sec",
      description: (
        <>
          <p><p>Deep sleep state associated with deep stillness where the body enters a natural healing mode.</p></p>
          <p>
            <p>
              Normally unconscious state, though advanced
              practitioners may momentarily touch this
              threshold while maintaining conscious
              awareness.
            </p>
          </p>
        </>
      ),
      icon: <Star className="w-6 h-6 text-purple-800" />,
      gradient: "from-purple-50 to-white",
      color: "purple",
      iconBg: "bg-purple-200",
      badgeClasses: "bg-purple-100 text-purple-700",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section
      ref={ref}
      id="brain-waves"
      className="relative py-16 overflow-hidden bg-white"
    >
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Modern Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            {/* Modern design border */}
            <div className="inline-block p-6 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl transform rotate-1 shadow-lg"></div>
              <div className="absolute inset-0 bg-gradient-to-l from-green-50 to-blue-50 rounded-2xl transform -rotate-1 shadow-lg"></div>

              <div className="relative bg-white p-6 rounded-2xl shadow-xl">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="w-8 h-[2px] bg-gradient-to-r from-blue-400 to-green-400"></div>
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-[0.2em] font-mono">
                    BRAIN WAVE PATTERNS
                  </span>
                  <div className="w-8 h-[2px] bg-gradient-to-l from-green-400 to-blue-400"></div>
                </div>

                <h2 className="font-heading text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
                  Yoga Nidra & Brain Wave Activity
                </h2>

                <div className="text-gray-500 font-medium uppercase tracking-wider text-xs">
                  ~ Understanding the NEUROScience ~
                </div>
              </div>
            </div>

            <p className="text-base text-gray-700 max-w-2xl mx-auto leading-relaxed mt-6 font-light italic">
              <p>
                "Discover how Yoga Nidra calms the active mind (beta
                waves) and gently transitions it into the deeply
                restful and intuitive realm of alpha–theta brainwaves"
              </p>
            </p>
          </motion.div>

          {/* Brain Wave Cards Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6"
          >
            {brainWaves.map((wave, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card
                  className={`group relative overflow-hidden transition-all duration-500 h-full rounded-lg shadow-md hover:shadow-xl hover:scale-105 bg-gradient-to-br ${wave.gradient}`}
                >
                  <CardContent className="p-6 h-full flex flex-col">
                    {/* Top section with icon */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="relative">
                        {/* Icon container */}
                        <div className={`w-12 h-12 ${wave.iconBg} rounded-lg flex items-center justify-center shadow border border-gray-100 group-hover:scale-110 transition-transform duration-300`}>
                          {wave.icon}
                        </div>
                      </div>

                      {/* Wave number */}
                      <div className="text-3xl font-bold text-gray-200 font-serif leading-none">
                        0{index + 1}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-gray-800 mb-2 leading-tight">
                        {wave.title}
                      </h3>

                      <div className="mb-3">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${wave.badgeClasses}`}>
                          {wave.frequency}
                        </span>
                      </div>

                      <p className="text-gray-600 leading-relaxed text-sm font-light">
                        {wave.description}
                      </p>
                    </div>

                    {/* Hover effect indicator */}
                    {/* <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Brain State
                        </span>
                        <div className={`w-2 h-2 rounded-full bg-${wave.color}-400 group-hover:scale-150 transition-transform duration-300`}></div>
                      </div>
                    </div> */}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BrainWaveActivity;
